import React from 'react';
import { MapPin, Film, Globe, GraduationCap, Sparkles, Users, Heart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const About = () => {
  const features = [
    {
      icon: MapPin,
      title: 'VENUE',
      description: 'Held in the vibrant city of Guwahati, Assam, India, this festival brings together a wide array of films from various Asian countries.'
    },
    {
      icon: Film,
      title: 'CINEMA',
      description: 'The festival aims to promote cultural exchange and foster a deeper understanding of the Asian experience through the powerful medium of cinema.'
    },
    {
      icon: Globe,
      title: 'CULTURE',
      description: 'Attendees can enjoy an eclectic mix of genres and styles, reflecting the rich cultural tapestry of the continent.'
    },
    {
      icon: GraduationCap,
      title: 'WORKSHOPS',
      description: 'In addition to film screenings, the festival features panel discussions, workshops, and interactive sessions with filmmakers, providing a dynamic platform for dialogue and learning.'
    }
  ];

  const values = [
    {
      icon: Sparkles,
      title: 'Innovation',
      description: 'Championing bold storytelling and fresh perspectives'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Building bridges between filmmakers and audiences'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Celebrating the art of cinema with dedication'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <Header />
      
      {/* Hero Section with Asian Pattern */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Decorative Asian Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff0000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        {/* Floating Decorative Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 border-2 border-red-500/30 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-32 w-16 h-16 bg-red-500/20 rounded-lg rotate-45"></div>
        <div className="absolute bottom-20 left-10 w-24 h-24 border-2 border-red-500/30 rotate-12"></div>
        <div className="absolute bottom-32 left-32 w-20 h-20 bg-red-500/10 rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="mb-8">
              <span className="inline-block px-6 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-red-500 text-sm uppercase tracking-widest mb-6">
                Celebrating Asian Cinema
              </span>
            </div>
            <h1 className="text-7xl md:text-9xl font-bold mb-8 leading-tight">
              ABOUT <span className="text-red-500">GAFF</span>
            </h1>
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="w-16 h-1 bg-gradient-to-r from-transparent to-red-500"></div>
              <div className="w-8 h-1 bg-red-500"></div>
              <div className="w-16 h-1 bg-gradient-to-l from-transparent to-red-500"></div>
            </div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Where Stories from the East Find Their Home
            </p>
          </div>
        </div>
      </section>

      {/* Introduction with Image */}
      <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black relative">
        {/* Side Pattern */}
        <div className="absolute left-0 top-0 bottom-0 w-1/4 opacity-5">
          <div className="h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #ff0000 0px, #ff0000 2px, transparent 2px, transparent 10px)',
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              {/* Left: Text Content */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                    A <span className="text-red-500">Celebration</span> of Asian Stories
                  </h2>
                  <div className="w-20 h-1 bg-red-500 mb-6"></div>
                </div>
                
                <p className="text-2xl leading-relaxed text-gray-300">
                  The Guwahati Asian Film Festival is an annual event dedicated to <span className="text-white font-semibold">celebrating and showcasing the diverse cinematic traditions of Asia.</span>
                </p>
                
                <p className="text-xl leading-relaxed text-gray-400">
                  By highlighting the creativity and stories of both established and emerging filmmakers, GAFF serves as a significant cultural event, contributing to the global appreciation of Asian cinema.
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 pt-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-red-500 mb-2">3</div>
                    <div className="text-sm uppercase tracking-wider text-gray-500">Editions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-red-500 mb-2">11</div>
                    <div className="text-sm uppercase tracking-wider text-gray-500">Countries</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-red-500 mb-2">50+</div>
                    <div className="text-sm uppercase tracking-wider text-gray-500">Films</div>
                  </div>
                </div>
              </div>

              {/* Right: Image Collage */}
              <div className="relative">
                <div className="relative">
                  {/* Main Image */}
                  <div className="relative rounded-2xl overflow-hidden border-4 border-red-500/30 shadow-2xl shadow-red-500/20">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_homepage-redesign-26/artifacts/hoynpfdi_Screenshot%202026-04-08%20at%208.47.40%E2%80%AFpm.png"
                      alt="Festival Moments"
                      className="w-full h-auto"
                    />
                  </div>
                  
                  {/* Decorative Corner Frame */}
                  <div className="absolute -top-4 -left-4 w-20 h-20 border-t-4 border-l-4 border-red-500"></div>
                  <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-4 border-r-4 border-red-500"></div>
                </div>
              </div>
            </div>

            {/* Quote Section */}
            <div className="relative">
              <div className="absolute -left-8 -top-8 text-9xl text-red-500/20 font-serif">"</div>
              <div className="bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent border-l-4 border-red-500 rounded-2xl p-12 backdrop-blur-sm">
                <p className="text-2xl md:text-3xl leading-relaxed text-gray-300 italic">
                  The festival promises to celebrate the diversity of Asian cinema, featuring a <span className="text-white font-semibold not-italic">dynamic selection of films</span> that showcase unique cultural narratives and innovative storytelling, fostering cross-cultural dialogue and appreciation among filmmakers and audiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section - Enhanced */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-red-950/10 to-black"></div>
        
        {/* Decorative Asian-inspired Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, #ff0000 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Decorative Circles */}
        <div className="absolute top-1/4 left-10 w-64 h-64 border border-red-500/20 rounded-full"></div>
        <div className="absolute bottom-1/4 right-10 w-80 h-80 border border-red-500/10 rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-1 bg-red-500"></div>
                  <span className="text-red-500 uppercase tracking-[0.3em] text-sm">Our Purpose</span>
                  <div className="w-12 h-1 bg-red-500"></div>
                </div>
              </div>
              <h2 className="text-6xl md:text-7xl font-bold mb-6">
                OUR <span className="text-red-500">MISSION</span>
              </h2>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-transparent rounded-3xl blur-3xl"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-3xl p-12 md:p-16 backdrop-blur-xl">
                <p className="text-2xl md:text-3xl leading-relaxed text-center text-gray-300">
                  The Guwahati Asian Film Festival's mission is to <span className="text-white font-semibold">celebrate and promote Asian cinema</span>, fostering cultural exchange, showcasing diverse talent, and inspiring local and global audiences. Through screenings, workshops, and community events, the festival aims to <span className="text-white font-semibold">bridge cultures and nurture the next generation of filmmakers.</span>
                </p>
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div key={index} className="text-center group">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/10 rounded-2xl mb-6 group-hover:bg-red-500/20 transition-all duration-300 group-hover:scale-110">
                      <Icon size={36} className="text-red-500" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 uppercase tracking-wide">{value.title}</h3>
                    <p className="text-gray-400">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid - Enhanced */}
      <section className="py-32 bg-gradient-to-b from-black to-zinc-950 relative">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(30deg, #ff0000 12%, transparent 12.5%, transparent 87%, #ff0000 87.5%, #ff0000), linear-gradient(150deg, #ff0000 12%, transparent 12.5%, transparent 87%, #ff0000 87.5%, #ff0000), linear-gradient(30deg, #ff0000 12%, transparent 12.5%, transparent 87%, #ff0000 87.5%, #ff0000), linear-gradient(150deg, #ff0000 12%, transparent 12.5%, transparent 87%, #ff0000 87.5%, #ff0000)',
            backgroundSize: '80px 140px',
            backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              WHAT WE <span className="text-red-500">OFFER</span>
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="w-16 h-1 bg-gradient-to-r from-transparent to-red-500"></div>
              <div className="w-8 h-1 bg-red-500"></div>
              <div className="w-16 h-1 bg-gradient-to-l from-transparent to-red-500"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-10 hover:border-red-500/50 hover:from-red-500/5 transition-all duration-500 overflow-hidden"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  {/* Glow Effect */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-red-500/10 rounded-full blur-3xl group-hover:bg-red-500/20 transition-all duration-500 -translate-y-1/2 translate-x-1/2"></div>
                  
                  <div className="relative z-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-red-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                      <Icon size={36} className="text-red-500" />
                    </div>
                    
                    <h3 className="text-3xl font-bold mb-4 uppercase tracking-wide group-hover:text-red-500 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    
                    <p className="text-gray-400 leading-relaxed text-lg">
                      {feature.description}
                    </p>
                  </div>

                  {/* Corner Decoration */}
                  <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-red-500/20 group-hover:border-red-500/50 transition-colors duration-300"></div>
                </div>
              );
            })}
          </div>
        </div>

        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </section>

      {/* Call to Action - Enhanced */}
      <section className="py-32 bg-gradient-to-b from-zinc-950 to-black relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0 L50 100 M0 50 L100 50' stroke='%23ff0000' stroke-width='0.5' fill='none'/%3E%3Ccircle cx='50' cy='50' r='30' stroke='%23ff0000' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-12">
              <div className="inline-block relative">
                <h2 className="text-5xl md:text-7xl font-bold mb-6">
                  JOIN US IN <span className="text-red-500">GUWAHATI</span>
                </h2>
                {/* Decorative Lines */}
                <div className="absolute -top-8 left-0 w-full flex justify-center">
                  <div className="w-32 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent"></div>
                </div>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
              Experience the magic of Asian cinema at our next edition
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="https://filmfreeway.com/GuwahatiAsianFilmFestival"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-block px-12 py-5 bg-gradient-to-r from-red-600 to-red-500 text-white text-lg font-bold uppercase tracking-wider rounded-xl hover:from-red-500 hover:to-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-500/30 hover:shadow-red-500/50"
              >
                <span className="flex items-center justify-center gap-2">
                  Submit Your Film
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </a>
              <a
                href="/#welcome"
                className="inline-block px-12 py-5 bg-white/5 text-white text-lg font-bold uppercase tracking-wider rounded-xl border-2 border-white/20 hover:bg-white/10 hover:border-red-500/50 transition-all duration-300 backdrop-blur-sm"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
